import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  ApiFailure,
  type AnalyzeResult,
  type DatasetSummary,
  type Filter,
  type Highlight,
  type RenderedDashboard,
  type Scorecard,
} from '../api';
import { useVizColors, type ThemeMode } from '../theme';
import { AutoDashboard } from './AutoDashboard';
import { AiChat } from './AiChat';
import { Notice } from './Panels';

/**
 * The AI analytics page.
 *
 * All the interaction state lives here — filters, search, the cross-filter —
 * and every change funnels through one debounced request that re-renders the
 * whole dashboard. That is the opposite of the usual arrangement, where each
 * chart owns its own query, and it is deliberate: with one request there is
 * exactly one set of rows on the page, so two blocks cannot disagree about a
 * number, and a filter change costs one round trip rather than twelve.
 */

type Phase = 'idle' | 'analysing' | 'ready';

interface DateRange {
  readonly from: string;
  readonly to: string;
}

export function AiAnalytics({ themeMode }: { themeMode: ThemeMode }) {
  const colors = useVizColors(themeMode);

  const [phase, setPhase] = useState<Phase>('idle');
  const [result, setResult] = useState<AnalyzeResult | null>(null);
  const [view, setView] = useState<RenderedDashboard | null>(null);
  const [error, setError] = useState<{ message: string; remedy: string } | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [ranges, setRanges] = useState<Record<string, DateRange>>({});
  const [search, setSearch] = useState('');
  const [crossFilter, setCrossFilter] = useState<{ column: string; value: string } | null>(null);

  const [existing, setExisting] = useState<DatasetSummary[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);

  // The chat answers questions through the saved report's narrate endpoint, so
  // it needs a report id. `analyzeFile` returns one; composing an existing
  // dataset does not, so it is looked up. `undefined` marks "still resolving".
  const [chatOpen, setChatOpen] = useState(false);
  const [chatReportId, setChatReportId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (result === null) {
      setChatReportId(undefined);
      setChatOpen(false);
      return;
    }
    if (result.reportId !== '') {
      setChatReportId(result.reportId);
      return;
    }
    let cancelled = false;
    api
      .listReports()
      .then((response) => {
        if (cancelled) return;
        setChatReportId(response.reports.find((r) => r.datasetId === result.datasetId)?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setChatReportId(null);
      });
    return () => {
      cancelled = true;
    };
  }, [result]);

  // A monotonic token, not a boolean. Filter changes can overlap, and without
  // this a slow early response lands after a fast later one and the page shows
  // the wrong data with the right filter chips.
  const requestToken = useRef(0);

  useEffect(() => {
    api
      .listDatasets()
      .then((response) => setExisting(response.datasets))
      .catch(() => setExisting([]));
  }, [phase]);

  // -- Filters ---------------------------------------------------------------

  const filters = useMemo<Filter[]>(() => {
    const out: Filter[] = [];

    for (const [column, values] of Object.entries(selections)) {
      if (values.length > 0) out.push({ column, operator: 'in', value: values });
    }
    for (const [column, range] of Object.entries(ranges)) {
      if (range.from !== '' && range.to !== '') {
        out.push({ column, operator: 'between', value: [range.from, `${range.to}T23:59:59.999Z`] });
      } else if (range.from !== '') {
        out.push({ column, operator: 'greaterThanOrEqual', value: range.from });
      } else if (range.to !== '') {
        out.push({ column, operator: 'lessThanOrEqual', value: `${range.to}T23:59:59.999Z` });
      }
    }
    // The cross-filter is a filter like any other, so it composes with the
    // controls instead of fighting them.
    if (crossFilter !== null) {
      out.push({ column: crossFilter.column, operator: 'equals', value: crossFilter.value });
    }
    return out;
  }, [selections, ranges, crossFilter]);

  const filterKey = JSON.stringify(filters);
  const hasFilters = filters.length > 0 || search.trim() !== '';

  // -- Re-render on interaction ---------------------------------------------

  useEffect(() => {
    if (result === null || view === null) return;
    if (!hasFilters && view.appliedFilters.length === 0 && view.rowCount === view.totalRowCount) {
      // Nothing to apply and nothing applied — skip the round trip entirely.
      return;
    }

    const token = ++requestToken.current;
    // Debounced, because a multi-select is several clicks and a search box is
    // several keystrokes. Firing per event would queue renders the user has
    // already moved past.
    const timer = setTimeout(() => {
      setBusy(true);
      api
        .renderDashboard(result.datasetId, result.dashboard.spec, filters, search)
        .then((response) => {
          if (token !== requestToken.current) return; // A newer request won.
          setView(response.dashboard);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (token !== requestToken.current) return;
          setError(describe(cause));
        })
        .finally(() => {
          if (token === requestToken.current) setBusy(false);
        });
    }, 260);

    return () => clearTimeout(timer);
    // `filterKey` rather than `filters`: the array identity changes on every
    // render, which would make this effect fire forever.
  }, [filterKey, search, result, hasFilters]);

  // -- Analysis --------------------------------------------------------------

  const analyse = useCallback((file: File) => {
    setPhase('analysing');
    setProgress(0);
    setError(null);
    setSelections({});
    setRanges({});
    setSearch('');
    setCrossFilter(null);

    api
      .analyzeFile(file, setProgress)
      .then((response) => {
        setResult(response);
        setView(response.dashboard);
        setPhase('ready');
      })
      .catch((cause: unknown) => {
        setError(describe(cause));
        setPhase('idle');
      });
  }, []);

  const openExisting = useCallback((datasetId: string) => {
    setPhase('analysing');
    setError(null);
    setSelections({});
    setRanges({});
    setSearch('');
    setCrossFilter(null);
    setShowLibrary(false);

    api
      .composeDashboard(datasetId)
      .then((response) => {
        setResult({
          datasetId: response.datasetId,
          reportId: '',
          fileName: response.fileName,
          structureInferred: false,
          warnings: response.warnings,
          dashboard: response.dashboard,
          elapsedMs: response.dashboard.elapsedMs,
        });
        setView(response.dashboard);
        setPhase('ready');
      })
      .catch((cause: unknown) => {
        setError(describe(cause));
        setPhase('idle');
      });
  }, []);

  const reset = () => {
    setPhase('idle');
    setResult(null);
    setView(null);
    setError(null);
  };

  // -------------------------------------------------------------------------

  if (phase === 'idle' || result === null || view === null) {
    return (
      <div className="page ai-page">
        <Intro
          onFile={analyse}
          error={error}
          existing={existing}
          showLibrary={showLibrary}
          onToggleLibrary={() => setShowLibrary(!showLibrary)}
          onOpen={openExisting}
          analysing={phase === 'analysing'}
          progress={progress}
        />
      </div>
    );
  }

  const spec = view.spec;

  return (
    <div className="page ai-page">
      <header className="ai-header">
        <div>
          <span className="badge accent">Analysed automatically</span>
          <h2>{spec.title}</h2>
          <p className="muted">
            {view.rowCount.toLocaleString()}
            {view.rowCount !== view.totalRowCount && ` of ${view.totalRowCount.toLocaleString()}`} rows ·{' '}
            {spec.blocks.length} blocks · composed in {result.elapsedMs.toLocaleString()}ms
          </p>
        </div>
        <div className="row">
          <button className="ghost" onClick={reset}>
            Analyse another file
          </button>
        </div>
      </header>

      {result.structureInferred && (
        <Notice
          tone="warning"
          message="The table layout in this file was inferred, not read."
          remedy="PDFs and log files do not declare their columns, so the boundaries were worked out from the text. Check the columns line up before relying on the figures."
        />
      )}

      {result.warnings.length > 0 && (
        <details className="ai-warnings">
          <summary>
            {result.warnings.length} note{result.warnings.length === 1 ? '' : 's'} from reading the file
          </summary>
          <ul>
            {result.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </details>
      )}

      <FilterBar
        spec={spec}
        selections={selections}
        ranges={ranges}
        search={search}
        crossFilter={crossFilter}
        busy={busy}
        rowCount={view.rowCount}
        totalRowCount={view.totalRowCount}
        onSearch={setSearch}
        onToggle={(column, value) =>
          setSelections((current) => {
            const chosen = current[column] ?? [];
            const next = chosen.includes(value)
              ? chosen.filter((entry) => entry !== value)
              : [...chosen, value];
            return { ...current, [column]: next };
          })
        }
        onRange={(column, range) => setRanges((current) => ({ ...current, [column]: range }))}
        onClearCrossFilter={() => setCrossFilter(null)}
        onClearAll={() => {
          setSelections({});
          setRanges({});
          setSearch('');
          setCrossFilter(null);
        }}
      />

      {error !== null && <Notice tone="error" message={error.message} remedy={error.remedy} />}

      <Summary view={view} onFollow={(highlight) => {
        if (highlight.drillDown !== null) {
          setCrossFilter(highlight.drillDown);
        }
        if (highlight.blockId !== null) {
          document.getElementById(`block-${highlight.blockId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }} />

      <AutoDashboard
        view={view}
        colors={colors}
        busy={busy}
        crossFilter={crossFilter}
        onCrossFilter={(column, value) =>
          setCrossFilter((current) =>
            // Clicking the selected mark again clears it. Without this the only
            // way out of a cross-filter is to find the chip, which is a long
            // way from the chart you just clicked.
            current !== null && current.column === column && current.value === value
              ? null
              : { column, value },
          )
        }
      />

      {/* Questions about what is on this page, over the same narrate endpoint
          the report's summary uses. The panel is collapsed by default: mounting
          the chat eagerly would keep a conversation alive on a screen built to
          be thrown away on the next file. */}
      <section className="card" style={{ marginTop: 16 }}>
        <header>
          <h2>Ask about this dashboard</h2>
          <button className="ghost" onClick={() => setChatOpen((open) => !open)} aria-expanded={chatOpen}>
            {chatOpen ? 'Hide' : 'Open chat'}
          </button>
        </header>
        {chatOpen && chatReportId === undefined && (
          <p className="muted">
            <span className="spinner" /> Preparing the chat…
          </p>
        )}
        {chatOpen && chatReportId === null && (
          <p className="muted">
            This dataset has no saved report to answer from. Re-open it from Sources — or upload the
            file again — and the chat will have an analysis to work with.
          </p>
        )}
        {chatOpen && chatReportId != null && (
          <AiChat
            embedded
            reportId={chatReportId}
            context={{ name: result.fileName, rowCount: view.totalRowCount, columnCount: null }}
          />
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Intro
// ---------------------------------------------------------------------------

const ACCEPTED = '.csv,.tsv,.txt,.xlsx,.xlsm,.json,.ndjson,.jsonl,.xml,.pdf,.log,.tab,.dat,.out';

function Intro({
  onFile,
  error,
  existing,
  showLibrary,
  onToggleLibrary,
  onOpen,
  analysing,
  progress,
}: {
  onFile: (file: File) => void;
  error: { message: string; remedy: string } | null;
  existing: DatasetSummary[];
  showLibrary: boolean;
  onToggleLibrary: () => void;
  onOpen: (id: string) => void;
  analysing: boolean;
  progress: number;
}) {
  const [dragging, setDragging] = useState(false);

  if (analysing) {
    return (
      <div className="ai-working">
        <div className="ai-spinner" aria-hidden />
        <h2>Reading the data</h2>
        {/* Naming the steps rather than showing an indeterminate bar: a user who
            can see what is happening waits; one staring at a spinner reloads. */}
        <ol className="ai-steps">
          <li className={progress < 1 ? 'active' : 'done'}>Uploading {Math.round(progress * 100)}%</li>
          <li className={progress >= 1 ? 'active' : ''}>Detecting structure and column types</li>
          <li className={progress >= 1 ? 'active' : ''}>Choosing what the data is about</li>
          <li className={progress >= 1 ? 'active' : ''}>Composing charts and finding what moved</li>
        </ol>
      </div>
    );
  }

  return (
    <>
      <header className="intro">
        <h2>AI analytics</h2>
        <p>
          Drop a file in. It works out what the columns are, what the data is about, and builds the
          dashboard — charts, KPIs, comparisons, drivers, anomalies and a forecast — with nothing to
          configure.
        </p>
      </header>

      {error !== null && <Notice tone="error" message={error.message} remedy={error.remedy} />}

      <label
        className={dragging ? 'ai-drop dragging' : 'ai-drop'}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files[0];
          if (file !== undefined) onFile(file);
        }}
      >
        <input
          type="file"
          accept={ACCEPTED}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) onFile(file);
          }}
        />
        <span className="ai-drop-glyph" aria-hidden>
          ⇪
        </span>
        <strong>Drop a file, or click to choose one</strong>
        <span className="muted">
          CSV · Excel · JSON · NDJSON · XML · PDF · logs and plain text
        </span>
      </label>

      <div className="ai-formats">
        <Format
          glyph="▦"
          name="Spreadsheets and CSV"
          note="Read directly. Column types, currencies, percentages and accounting negatives are all coerced before profiling."
        />
        <Format
          glyph="{ }"
          name="JSON and NDJSON"
          note="The record array is located inside the envelope rather than assumed, nested objects flatten to dotted columns, and the path it used is reported back."
        />
        <Format
          glyph="< >"
          name="XML"
          note="The repeating element becomes the row. External entities are never resolved, so an uploaded file cannot read the server's filesystem."
        />
        <Format
          glyph="¶"
          name="PDF, logs and text"
          note="Structure is inferred from glyph positions or line patterns. Always flagged as inferred, because a guessed table you trust is worse than none."
        />
      </div>

      {existing.length > 0 && (
        <div className="ai-library">
          <button className="ghost" onClick={onToggleLibrary} aria-expanded={showLibrary}>
            {showLibrary ? 'Hide' : `Or analyse one of your ${existing.length} existing datasets`}
          </button>
          {showLibrary && (
            <ul>
              {existing.map((dataset) => (
                <li key={dataset.id}>
                  <button onClick={() => onOpen(dataset.id)}>
                    <strong>{dataset.name}</strong>
                    <span className="muted">
                      {dataset.rowCount.toLocaleString()} rows · {dataset.columnCount} columns
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

function Format({ glyph, name, note }: { glyph: string; name: string; note: string }) {
  return (
    <article className="ai-format">
      <span className="ai-format-glyph" aria-hidden>
        {glyph}
      </span>
      <div>
        <h4>{name}</h4>
        <p className="muted">{note}</p>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Filter bar
// ---------------------------------------------------------------------------

function FilterBar({
  spec,
  selections,
  ranges,
  search,
  crossFilter,
  busy,
  rowCount,
  totalRowCount,
  onSearch,
  onToggle,
  onRange,
  onClearCrossFilter,
  onClearAll,
}: {
  spec: RenderedDashboard['spec'];
  selections: Record<string, string[]>;
  ranges: Record<string, DateRange>;
  search: string;
  crossFilter: { column: string; value: string } | null;
  busy: boolean;
  rowCount: number;
  totalRowCount: number;
  onSearch: (value: string) => void;
  onToggle: (column: string, value: string) => void;
  onRange: (column: string, range: DateRange) => void;
  onClearCrossFilter: () => void;
  onClearAll: () => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const active =
    Object.values(selections).reduce((sum, values) => sum + values.length, 0) +
    Object.values(ranges).filter((range) => range.from !== '' || range.to !== '').length +
    (crossFilter === null ? 0 : 1) +
    (search.trim() === '' ? 0 : 1);

  return (
    <div className="filter-bar">
      <div className="filter-row">
        <div className="filter-search">
          <span aria-hidden>⌕</span>
          <input
            type="search"
            value={search}
            placeholder={
              spec.searchColumns.length === 0
                ? 'No text columns to search'
                : `Search ${spec.searchColumns.slice(0, 3).join(', ')}${spec.searchColumns.length > 3 ? '…' : ''}`
            }
            disabled={spec.searchColumns.length === 0}
            onChange={(event) => onSearch(event.target.value)}
          />
        </div>

        {spec.filters.map((control) =>
          control.kind === 'dateRange' ? (
            <div className="filter-dates" key={control.column}>
              <label>
                <span className="muted">{control.label} from</span>
                <input
                  type="date"
                  min={control.min}
                  max={control.max}
                  value={ranges[control.column]?.from ?? ''}
                  onChange={(event) =>
                    onRange(control.column, {
                      from: event.target.value,
                      to: ranges[control.column]?.to ?? '',
                    })
                  }
                />
              </label>
              <label>
                <span className="muted">to</span>
                <input
                  type="date"
                  min={control.min}
                  max={control.max}
                  value={ranges[control.column]?.to ?? ''}
                  onChange={(event) =>
                    onRange(control.column, {
                      from: ranges[control.column]?.from ?? '',
                      to: event.target.value,
                    })
                  }
                />
              </label>
            </div>
          ) : (
            <div className="filter-select" key={control.column}>
              <button
                className={(selections[control.column]?.length ?? 0) > 0 ? 'chip active' : 'chip'}
                onClick={() => setOpen(open === control.column ? null : control.column)}
                aria-expanded={open === control.column}
              >
                {control.label}
                {(selections[control.column]?.length ?? 0) > 0 && (
                  <span className="chip-count">{selections[control.column]!.length}</span>
                )}
                <span aria-hidden>▾</span>
              </button>

              {open === control.column && (
                <div className="filter-menu">
                  {control.truncated && (
                    <p className="muted filter-note">
                      Showing the first {control.options.length} values — use search for the rest.
                    </p>
                  )}
                  <div className="filter-options">
                    {control.options.map((option) => (
                      <label key={option}>
                        <input
                          type="checkbox"
                          checked={selections[control.column]?.includes(option) ?? false}
                          onChange={() => onToggle(control.column, option)}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ),
        )}

        <span className="filter-status">
          {busy ? (
            <>
              <span className="spinner" /> updating
            </>
          ) : (
            <>
              <strong>{rowCount.toLocaleString()}</strong>
              {rowCount !== totalRowCount && <span className="muted"> of {totalRowCount.toLocaleString()}</span>} rows
            </>
          )}
        </span>

        {active > 0 && (
          <button className="ghost tiny" onClick={onClearAll}>
            Clear all
          </button>
        )}
      </div>

      {crossFilter !== null && (
        <div className="cross-filter">
          <span className="muted">Cross-filtered by a chart:</span>
          <button className="chip active" onClick={onClearCrossFilter}>
            {crossFilter.column} = {crossFilter.value}
            <span aria-hidden>×</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

function Summary({
  view,
  onFollow,
}: {
  view: RenderedDashboard;
  onFollow: (highlight: Highlight) => void;
}) {
  const [tab, setTab] = useState<'summary' | 'findings' | 'actions'>('summary');

  return (
    <section className="ai-summary">
      <div className="ai-tabs" role="tablist">
        <button role="tab" aria-selected={tab === 'summary'} onClick={() => setTab('summary')}>
          Executive summary
        </button>
        <button role="tab" aria-selected={tab === 'findings'} onClick={() => setTab('findings')}>
          What stands out{' '}
          <span className="tab-count">{view.highlights.length}</span>
        </button>
        <button role="tab" aria-selected={tab === 'actions'} onClick={() => setTab('actions')}>
          What to do{' '}
          <span className="tab-count">{view.recommendations.length}</span>
        </button>
      </div>

      {tab === 'summary' && <ExecutiveSummary view={view} onFollow={onFollow} />}

      {tab === 'findings' && (
        <div className="findings">
          {view.highlights.length === 0 ? (
            <p className="muted">
              Nothing stands out under these filters: no group dominates, nothing fell outside the model,
              and the data is largely complete.
            </p>
          ) : (
            view.highlights.map((highlight) => (
              <article className={`finding ${highlight.severity}`} key={highlight.id}>
                <header>
                  <span className="finding-glyph" aria-hidden>
                    {glyphFor(highlight.kind, highlight.severity)}
                  </span>
                  <span className={`badge ${badgeFor(highlight.severity)}`}>{highlight.kind}</span>
                  <h4>{highlight.headline}</h4>
                </header>
                <p>{highlight.detail}</p>

                <footer>
                  {/* Every figure quoted above, with the statistic that produced
                      it. A finding you cannot check is a finding you have to
                      take on faith. */}
                  <details>
                    <summary>Evidence ({highlight.evidence.length})</summary>
                    <ul>
                      {highlight.evidence.map((item, index) => (
                        <li key={index}>
                          <span className="muted">{item.label}</span>
                          <strong>
                            {item.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                          </strong>
                          <span className="muted">{item.statistic}</span>
                        </li>
                      ))}
                    </ul>
                  </details>

                  {(highlight.blockId !== null || highlight.drillDown !== null) && (
                    <button className="ghost tiny" onClick={() => onFollow(highlight)}>
                      {highlight.drillDown === null ? 'Show me' : `Filter to ${highlight.drillDown.value}`} →
                    </button>
                  )}
                </footer>
              </article>
            ))
          )}
        </div>
      )}

      {tab === 'actions' && (
        <ol className="ai-actions">
          {view.recommendations.map((recommendation, index) => (
            <li key={index}>{recommendation}</li>
          ))}
        </ol>
      )}
    </section>
  );
}

function badgeFor(severity: Highlight['severity']): string {
  return severity === 'warning' ? 'warning' : severity === 'notable' ? 'accent' : '';
}

/**
 * The executive summary is a scannable digest, not a second copy of the prose
 * narrative. It leads with the numbers, then the few findings that matter, then
 * the health of the data — and tucks the full narrative away for readers who
 * want it. Every figure still comes from `view`, so nothing here is invented.
 */
function ExecutiveSummary({
  view,
  onFollow,
}: {
  view: RenderedDashboard;
  onFollow: (highlight: Highlight) => void;
}) {
  const kpiBlock = view.blocks.find((block) => block.spec.kind === 'kpi');
  const cards: Scorecard[] =
    kpiBlock !== undefined && kpiBlock.data !== null && kpiBlock.data.kind === 'kpi'
      ? kpiBlock.data.cards
      : [];

  // The gist is the findings that are not merely informational: what moved,
  // what broke, what dominates. A summary that relists everything is a list.
  const takeaways = view.highlights.filter((highlight) => highlight.severity !== 'info').slice(0, 4);

  return (
    <div className="exec-summary">
      {cards.length > 0 && (
        <section className="exec-section">
          <h3 className="exec-title">At a glance</h3>
          <div className="kpi-row">
            {cards.slice(0, 4).map((card) => (
              <article className="kpi" key={card.id}>
                <span className="kpi-title">{card.title}</span>
                <span className="kpi-value">{card.formatted}</span>
                <span className="kpi-foot">
                  {card.trend === null ? (
                    <span className="muted">{card.subtitle}</span>
                  ) : (
                    <>
                      <span className={`delta ${card.trend.direction}`}>
                        {card.trend.direction === 'up' ? '▲' : card.trend.direction === 'down' ? '▼' : '■'}{' '}
                        {Math.abs(card.trend.changePercent).toFixed(1)}%
                      </span>
                      <span className="muted"> {card.trend.basis}</span>
                    </>
                  )}
                </span>
              </article>
            ))}
          </div>
        </section>
      )}

      {takeaways.length > 0 && (
        <section className="exec-section">
          <h3 className="exec-title">Key takeaways</h3>
          <ul className="takeaways">
            {takeaways.map((highlight) => (
              <li className={`takeaway ${highlight.severity}`} key={highlight.id}>
                <span className="takeaway-glyph" aria-hidden>
                  {glyphFor(highlight.kind, highlight.severity)}
                </span>
                <div className="takeaway-body">
                  <strong>{highlight.headline}</strong>
                  <span className="takeaway-detail">{highlight.detail}</span>
                </div>
                {(highlight.blockId !== null || highlight.drillDown !== null) && (
                  <button className="ghost tiny takeaway-follow" onClick={() => onFollow(highlight)}>
                    {highlight.drillDown === null ? 'Show me' : 'Filter →'}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <HealthFacts view={view} />

      {/* The full narrative is kept for readers who want prose, but it is not
          the default: bullet points scan faster than paragraphs do. */}
      <details className="exec-narrative">
        <summary>Full narrative</summary>
        <div className="exec-prose">
          {view.summary.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
          <p className="muted ai-provenance">
            Written from the computed findings. Every figure above appears in the evidence attached to a
            finding — nothing here was generated by a language model.
          </p>
        </div>
      </details>
    </div>
  );
}

/** Quality findings carry the data-health figures in their evidence. */
function HealthFacts({ view }: { view: RenderedDashboard }) {
  const facts: { label: string; value: string }[] = [];

  for (const highlight of view.highlights) {
    if (highlight.kind !== 'quality') continue;

    const completeness = highlight.evidence.find((entry) => entry.label === 'completeness');
    if (completeness !== undefined) {
      facts.push({ label: 'cells populated', value: `${completeness.value.toFixed(0)}%` });
    }
    const missing = highlight.evidence.find((entry) => entry.label === 'missing cells');
    if (missing !== undefined) {
      facts.push({ label: 'missing cells', value: missing.value.toLocaleString('en-US') });
    }
    const duplicates = highlight.evidence.find((entry) => entry.label === 'duplicate rows');
    if (duplicates !== undefined) {
      facts.push({ label: 'duplicate rows', value: duplicates.value.toLocaleString('en-US') });
    }
  }

  if (facts.length === 0) return null;

  return (
    <section className="exec-section">
      <h3 className="exec-title">Data health</h3>
      <div className="exec-health">
        {facts.map((fact) => (
          <span className="health-chip" key={fact.label}>
            <strong>{fact.value}</strong>
            <span className="muted">{fact.label}</span>
          </span>
        ))}
      </div>
    </section>
  );
}

/** A compact, severity-aware glyph for a finding, shared by findings and takeaways. */
function glyphFor(kind: Highlight['kind'], severity: Highlight['severity']): string {
  if (severity === 'warning') return '⚠';
  switch (kind) {
    case 'trend':
      return '↗';
    case 'anomaly':
      return '◉';
    case 'driver':
      return '⇢';
    case 'concentration':
      return '◔';
    case 'risk':
      return '▼';
    case 'opportunity':
      return '▲';
    case 'quality':
      return '◷';
  }
}

function describe(cause: unknown): { message: string; remedy: string } {
  if (cause instanceof ApiFailure) return { message: cause.message, remedy: cause.remedy };
  return {
    message: cause instanceof Error ? cause.message : 'Something went wrong.',
    remedy: 'Try again, or pick a different file.',
  };
}
